import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useFirestore } from '../hooks/useFirestore';
import { useConfirmation } from '../hooks/useConfirmation';
import DomainCard from '../components/Domains/DomainCard';
import DomainModal from '../components/Domains/DomainModal';
import ConfirmationModal from '../components/Common/ConfirmationModal';
import Breadcrumbs from '../components/Navigation/Breadcrumbs';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiSearch, FiArrowDown, FiArrowUp, FiTrash2, FiShield, FiAlertTriangle, FiCalendar, FiGrid, FiColumns, FiSquare } = FiIcons;

const Domains = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('authority'); // authority, earnings, name, articles, spam_score, age
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
  const [gridLayout, setGridLayout] = useState(3); // 1, 3, or 4 columns

  const { documents: domains, loading, addDocument, updateDocument, deleteDocument } = useFirestore('domains');
  const { documents: articles } = useFirestore('articles');
  const { confirmationState, showConfirmation, closeConfirmation } = useConfirmation();

  const handleAddDomain = () => {
    setEditingDomain(null);
    setIsModalOpen(true);
  };

  const handleEditDomain = (domain) => {
    console.log('Editing domain:', domain); // Debug log
    setEditingDomain(domain);
    setIsModalOpen(true);
  };

  const handleSaveDomain = async (domainData) => {
    try {
      console.log('Saving domain data:', domainData); // Debug log
      if (editingDomain) {
        console.log('Updating domain with ID:', editingDomain.id); // Debug log
        await updateDocument(editingDomain.id, domainData);
        toast.success('Domain updated successfully!');
      } else {
        console.log('Creating new domain'); // Debug log
        await addDocument(domainData);
        toast.success('Domain created successfully!');
      }
      setIsModalOpen(false);
      setEditingDomain(null);
    } catch (error) {
      console.error('Error saving domain:', error);
      toast.error('Error saving domain: ' + error.message);
    }
  };

  const handleDeleteDomain = async (domain) => {
    const articleCount = articles.filter(article => article.domain_id === domain.id).length;
    
    const message = articleCount > 0 
      ? `This domain has ${articleCount} articles. Are you sure you want to delete "${domain.name}"? This will also remove the domain reference from all articles and cannot be undone.`
      : `Are you sure you want to delete "${domain.name}"? This action cannot be undone.`;

    const confirmed = await showConfirmation({
      title: 'Delete Domain',
      message,
      confirmText: 'Delete Domain',
      cancelText: 'Keep Domain',
      type: 'danger',
      icon: FiTrash2,
      onConfirm: async () => {
        await deleteDocument(domain.id);
        toast.success('Domain deleted successfully!');
      }
    });

    if (!confirmed) {
      toast.error('Failed to delete domain');
    }
  };

  const getDomainStats = (domainId) => {
    const domainArticles = articles.filter(article => article.domain_id === domainId);
    const articleCount = domainArticles.length;
    const totalEarnings = domainArticles.reduce((sum, article) => sum + (article.price || 0), 0);
    return { articleCount, totalEarnings };
  };

  const calculateDomainAge = (registrationDate) => {
    if (!registrationDate) return 0;
    const regDate = new Date(registrationDate);
    const today = new Date();
    const diffTime = Math.abs(today - regDate);
    const diffYears = diffTime / (1000 * 60 * 60 * 24 * 365.25);
    return diffYears;
  };

  const handleSort = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder(newSortBy === 'authority' ? 'desc' : 'desc');
    }
  };

  const getGridClasses = () => {
    switch (gridLayout) {
      case 1:
        return 'grid grid-cols-1 gap-6';
      case 3:
        return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6';
      case 4:
        return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6';
      default:
        return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6';
    }
  };

  const getLayoutIcon = (layout) => {
    switch (layout) {
      case 1:
        return FiSquare;
      case 3:
        return FiColumns;
      case 4:
        return FiGrid;
      default:
        return FiColumns;
    }
  };

  const filteredAndSortedDomains = domains
    .filter(domain =>
      domain.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      domain.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (domain.niche && domain.niche.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .map(domain => ({
      ...domain,
      stats: getDomainStats(domain.id),
      age: calculateDomainAge(domain.registration_date)
    }))
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'authority':
          aValue = a.domain_authority || 0;
          bValue = b.domain_authority || 0;
          break;
        case 'spam_score':
          aValue = a.spam_score || 0;
          bValue = b.spam_score || 0;
          break;
        case 'earnings':
          aValue = a.stats.totalEarnings;
          bValue = b.stats.totalEarnings;
          break;
        case 'articles':
          aValue = a.stats.articleCount;
          bValue = b.stats.articleCount;
          break;
        case 'age':
          aValue = a.age;
          bValue = b.age;
          break;
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        default:
          aValue = a.domain_authority || 0;
          bValue = b.domain_authority || 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const totalEarnings = domains.reduce((sum, domain) => {
    const { totalEarnings } = getDomainStats(domain.id);
    return sum + totalEarnings;
  }, 0);

  const averageAuthority = domains.length > 0
    ? domains.reduce((sum, domain) => sum + (domain.domain_authority || 0), 0) / domains.length
    : 0;

  const lowSpamDomains = domains.filter(domain => (domain.spam_score || 0) <= 10).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading domains...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Publication Domains</h1>
          <p className="text-gray-600">Manage domains where your articles are published</p>
        </div>
        <button
          onClick={handleAddDomain}
          className="mt-4 sm:mt-0 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
        >
          <SafeIcon icon={FiPlus} className="w-5 h-5" />
          <span>Add Domain</span>
        </button>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Domains</h3>
          <p className="text-3xl font-bold text-primary-600">{domains.length}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Avg Authority</h3>
          <p className="text-3xl font-bold text-warning-600">{averageAuthority.toFixed(1)}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Low Spam Score</h3>
          <p className="text-3xl font-bold text-success-600">{lowSpamDomains}</p>
          <p className="text-sm text-gray-500">≤10% spam score</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Earnings</h3>
          <p className="text-3xl font-bold text-purple-600">{totalEarnings.toFixed(2)} LEI</p>
        </motion.div>
      </div>

      {/* Search, Sort Controls and Layout Options */}
      <div className="flex flex-col lg:flex-row gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="relative flex-1"
        >
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search domains..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </motion.div>

        {/* Sort Controls */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => handleSort('authority')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
              sortBy === 'authority'
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <SafeIcon icon={FiShield} className="w-4 h-4" />
            <span>Authority</span>
            {sortBy === 'authority' && (
              <SafeIcon icon={sortOrder === 'asc' ? FiArrowUp : FiArrowDown} className="w-4 h-4" />
            )}
          </button>

          <button
            onClick={() => handleSort('age')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
              sortBy === 'age'
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <SafeIcon icon={FiCalendar} className="w-4 h-4" />
            <span>Age</span>
            {sortBy === 'age' && (
              <SafeIcon icon={sortOrder === 'asc' ? FiArrowUp : FiArrowDown} className="w-4 h-4" />
            )}
          </button>

          <button
            onClick={() => handleSort('spam_score')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
              sortBy === 'spam_score'
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <SafeIcon icon={FiAlertTriangle} className="w-4 h-4" />
            <span>Spam Score</span>
            {sortBy === 'spam_score' && (
              <SafeIcon icon={sortOrder === 'asc' ? FiArrowUp : FiArrowDown} className="w-4 h-4" />
            )}
          </button>

          <button
            onClick={() => handleSort('earnings')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
              sortBy === 'earnings'
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <span>Earnings</span>
            {sortBy === 'earnings' && (
              <SafeIcon icon={sortOrder === 'asc' ? FiArrowUp : FiArrowDown} className="w-4 h-4" />
            )}
          </button>

          <button
            onClick={() => handleSort('articles')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
              sortBy === 'articles'
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <span>Articles</span>
            {sortBy === 'articles' && (
              <SafeIcon icon={sortOrder === 'asc' ? FiArrowUp : FiArrowDown} className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Layout Options */}
        <div className="flex items-center space-x-1 bg-white border border-gray-300 rounded-lg p-1">
          <span className="text-sm text-gray-600 px-2">View:</span>
          
          <button
            onClick={() => setGridLayout(1)}
            className={`p-2 rounded transition-colors ${
              gridLayout === 1
                ? 'bg-primary-600 text-white'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
            title="1 column"
          >
            <SafeIcon icon={FiSquare} className="w-4 h-4" />
          </button>

          <button
            onClick={() => setGridLayout(3)}
            className={`p-2 rounded transition-colors ${
              gridLayout === 3
                ? 'bg-primary-600 text-white'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
            title="3 columns"
          >
            <SafeIcon icon={FiColumns} className="w-4 h-4" />
          </button>

          <button
            onClick={() => setGridLayout(4)}
            className={`p-2 rounded transition-colors ${
              gridLayout === 4
                ? 'bg-primary-600 text-white'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
            title="4 columns"
          >
            <SafeIcon icon={FiGrid} className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Domains Grid */}
      <div className={getGridClasses()}>
        {filteredAndSortedDomains.map((domain, index) => (
          <motion.div
            key={domain.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <DomainCard
              domain={domain}
              onEdit={handleEditDomain}
              onDelete={() => handleDeleteDomain(domain)}
              articleCount={domain.stats.articleCount}
              totalEarnings={domain.stats.totalEarnings}
            />
          </motion.div>
        ))}
      </div>

      {filteredAndSortedDomains.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <p className="text-gray-500 text-lg">No domains found</p>
          <p className="text-gray-400 mt-2">
            {searchTerm
              ? 'Try adjusting your search terms'
              : 'Add your first publication domain to get started'}
          </p>
        </motion.div>
      )}

      <DomainModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveDomain}
        domain={editingDomain}
      />

      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        onClose={closeConfirmation}
        onConfirm={confirmationState.onConfirm}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
        icon={confirmationState.icon}
        isLoading={confirmationState.isLoading}
      />
    </div>
  );
};

export default Domains;